import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Calendar, Clock, Check, Loader2, ArrowRight, Heart, Star } from "lucide-react";
import axios from "axios";

const LOGO = "/upwardcast-logo-transparent.png";
const ATTENDEE_TOKEN_KEY = "uc_attendee_token";

function fmtDate(iso) {
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    weekday: "long", month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
  });
}
const modeLabel = (mode) => (mode === "live" ? "Live gathering" : "Anytime experience");

export default function WebinarRegister() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [exp, setExp] = useState(null);
  const [selected, setSelected] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [reviewSummary, setReviewSummary] = useState(null);

  useEffect(() => {
    api
      .get(`/public/webinars/${slug}`)
      .then((r) => {
        setExp(r.data);
        if (r.data.sessions?.length) setSelected(r.data.sessions[0]);
      })
      .catch(() => setExp(false));
    api.get(`/public/webinars/${slug}/reviews`).then((r) => setReviewSummary(r.data)).catch(() => {});
  }, [slug]);

  const handleReserve = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await api.post(`/public/webinars/${slug}/register`, { name, email, session_start: selected, origin: window.location.origin });
      if (res.data.requires_payment) {
        const checkout = await api.post(`/public/webinars/${slug}/checkout`, {
          registrant_id: res.data.registrant_id,
          origin: window.location.origin,
        });
        window.location.href = checkout.data.url;
      } else {
        toast.success("Your spot is reserved. Welcome 🌿");
        navigate(`/watch/${res.data.registrant_id}`);
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Something went wrong");
      setSubmitting(false);
    }
  };

  if (exp === null)
    return (
      <div className="wellness min-h-screen bg-[#FBFAFF] flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-[#7B2CBF]" />
      </div>
    );
  if (exp === false)
    return (
      <div className="wellness min-h-screen bg-[#FBFAFF] flex flex-col items-center justify-center gap-4">
        <p className="text-xl">This experience could not be found.</p>
        <Link to="/" className="text-[#7B2CBF] font-medium">← Back home</Link>
      </div>
    );

  return (
    <div className="wellness min-h-screen bg-[#FBFAFF]" data-testid="register-page">
      <header className="border-b border-[#E5E0F5]">
        <div className="max-w-6xl mx-auto px-6 h-20 flex items-center">
          <Link to="/" className="flex items-center">
            <img src={LOGO} alt="UpwardCast" className="h-8 w-auto" />
          </Link>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 py-14 grid grid-cols-1 lg:grid-cols-2 gap-14">
        {/* Left */}
        <div className="animate-fade-up">
          {exp.image_url && (
            <div className="overflow-hidden rounded-[2rem] soft-shadow mb-8">
              <img src={exp.image_url} alt={exp.title} className="w-full h-72 object-cover" />
            </div>
          )}
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-[#7B2CBF] mb-4">
            {modeLabel(exp.mode)}
          </div>
          <h1 className="text-4xl sm:text-5xl font-light leading-tight mb-5">{exp.title}</h1>
          {reviewSummary && reviewSummary.count > 0 && (
            <div className="flex items-center gap-2 mb-5 -mt-2" data-testid="rating-badge">
              <Stars value={Math.round(reviewSummary.average)} />
              <span className="text-sm font-medium text-[#6A6588]">{reviewSummary.average} from {reviewSummary.count} attendee{reviewSummary.count === 1 ? "" : "s"}</span>
            </div>
          )}
          <p className="text-lg text-[#6A6588] leading-relaxed mb-8">{exp.description}</p>

          {exp.hosts?.length > 0 && (
            <div className="border-t border-[#E5E0F5] pt-8">
              <div className="text-xs uppercase tracking-[0.2em] text-[#16B6A4] mb-4">
                {exp.hosts.length > 1 ? "Your guides" : "Your guide"}
              </div>
              <div className="space-y-5">
                {exp.hosts.map((h) => (
                  <div key={h.id} className="flex items-start gap-4">
                    <div className="w-14 h-14 rounded-full bg-[#E5E0F5] overflow-hidden flex items-center justify-center font-display text-xl text-[#7B2CBF] shrink-0">
                      {h.avatar_url ? <img src={h.avatar_url} alt={h.name} className="w-full h-full object-cover" /> : h.name?.[0]}
                    </div>
                    <div>
                      <div className="font-medium text-[#211C36]">{h.name}</div>
                      <div className="text-sm text-[#6A6588]">{h.title}</div>
                      {h.bio && <p className="text-sm text-[#6A6588] mt-1 leading-relaxed">{h.bio}</p>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Right */}
        <div className="animate-fade-up" style={{ animationDelay: "0.1s" }}>
          <div className="bg-white rounded-[2rem] soft-shadow p-8 lg:sticky lg:top-8">
            <div className="flex items-baseline justify-between mb-7">
              <h2 className="text-3xl font-light">Reserve your spot</h2>
              <div className="font-display text-3xl text-gradient">
                {exp.price > 0 ? `$${exp.price.toFixed(0)}` : "Free"}
              </div>
            </div>

            <form onSubmit={handleReserve} className="space-y-6">
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-[#6A6588] mb-3 flex items-center gap-1.5">
                  <Calendar className="w-3.5 h-3.5" /> Choose a time
                </label>
                <div className="grid grid-cols-1 gap-2.5 max-h-56 overflow-y-auto pr-1">
                  {exp.sessions?.map((s) => (
                    <button
                      type="button"
                      key={s}
                      data-testid={`session-option-${s}`}
                      onClick={() => setSelected(s)}
                      className={`text-left px-5 py-3.5 rounded-2xl text-sm transition-all duration-300 flex items-center justify-between ${
                        selected === s
                          ? "btn-gradient text-white"
                          : "bg-[#F1EEFB] text-[#211C36] hover:bg-[#E5E0F5]"
                      }`}
                    >
                      <span className="flex items-center gap-2">
                        <Clock className="w-3.5 h-3.5 opacity-70" /> {fmtDate(s)}
                      </span>
                      {selected === s && <Check className="w-4 h-4" />}
                    </button>
                  ))}
                </div>
              </div>

              <input
                required
                data-testid="register-name-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your name"
                className="w-full bg-[#F1EEFB] rounded-2xl px-5 py-3.5 text-sm placeholder-[#9A95B5] focus:outline-none focus:ring-2 focus:ring-[#7B2CBF]/40"
              />
              <input
                required
                type="email"
                data-testid="register-email-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Email address"
                className="w-full bg-[#F1EEFB] rounded-2xl px-5 py-3.5 text-sm placeholder-[#9A95B5] focus:outline-none focus:ring-2 focus:ring-[#7B2CBF]/40"
              />

              <button
                type="submit"
                disabled={submitting || !selected}
                data-testid="register-submit-btn"
                className="w-full btn-gradient text-white disabled:opacity-50 px-6 py-4 rounded-full font-medium flex items-center justify-center gap-2 hover:-translate-y-0.5"
              >
                {submitting ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    {exp.price > 0 ? "Reserve & continue" : "Reserve my spot"}
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>
              <p className="text-center text-xs text-[#9A95B5] flex items-center justify-center gap-1.5">
                <Heart className="w-3 h-3 text-[#16B6A4]" /> A gentle reminder will reach your inbox.
              </p>
            </form>
          </div>
        </div>
      </div>

      <ReviewsSection webinarId={exp.id} slug={slug} />
    </div>
  );
}

function Stars({ value, onChange }) {
  return (
    <div className="flex items-center gap-1">
      {[1, 2, 3, 4, 5].map((n) => (
        <button key={n} type="button" disabled={!onChange} onClick={() => onChange && onChange(n)} data-testid={onChange ? `star-${n}` : undefined}
          className={onChange ? "cursor-pointer" : "cursor-default"}>
          <Star className={`w-4 h-4 ${n <= value ? "fill-[#F2B705] text-[#F2B705]" : "text-[#D9D3EC]"}`} />
        </button>
      ))}
    </div>
  );
}

function ReviewsSection({ webinarId, slug }) {
  const [data, setData] = useState(null);
  const [elig, setElig] = useState(null);
  const [rating, setRating] = useState(5);
  const [body, setBody] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const token = typeof window !== "undefined" && localStorage.getItem(ATTENDEE_TOKEN_KEY);
  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

  const load = () => api.get(`/public/webinars/${slug}/reviews`).then((r) => setData(r.data)).catch(() => setData({ count: 0, average: 0, reviews: [] }));
  useEffect(() => {
    load();
    if (token) {
      axios.get(`${API}/account/review-eligibility?webinar_id=${webinarId}`, { headers: { Authorization: `Bearer ${token}` } })
        .then((r) => setElig(r.data)).catch(() => setElig(null));
    }
  }, [webinarId, slug]); // eslint-disable-line

  const submit = async () => {
    if (!body.trim()) return toast.error("Please write a few words");
    setSubmitting(true);
    try {
      await axios.post(`${API}/account/reviews`, { webinar_id: webinarId, rating, body }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Thank you for sharing 🌿");
      setBody(""); load(); setElig({ ...elig, already_reviewed: true });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not submit review");
    }
    setSubmitting(false);
  };

  if (!data || data.count === 0) {
    // Still show the write box for eligible attendees even with no reviews
    if (!(token && elig?.eligible && !elig?.already_reviewed)) return null;
  }

  return (
    <div className="max-w-6xl mx-auto px-6 pb-20" data-testid="reviews-section">
      <div className="border-t border-[#E5E0F5] pt-12">
        <div className="flex items-center gap-3 mb-8">
          <h2 className="text-3xl font-light">What people felt</h2>
          {data && data.count > 0 && (
            <div className="flex items-center gap-2 text-[#6A6588]">
              <Stars value={Math.round(data.average)} />
              <span className="text-sm" data-testid="reviews-average">{data.average} · {data.count} review{data.count === 1 ? "" : "s"}</span>
            </div>
          )}
        </div>

        {token && elig?.eligible && !elig?.already_reviewed && (
          <div className="bg-white rounded-3xl soft-shadow p-6 mb-8 max-w-xl" data-testid="review-form">
            <div className="font-medium mb-3">Share your experience</div>
            <div className="mb-3"><Stars value={rating} onChange={setRating} /></div>
            <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={3} placeholder="How did this experience land for you?" data-testid="review-body-input"
              className="w-full bg-[#F1EEFB] rounded-2xl px-4 py-3 text-sm placeholder-[#9A95B5] focus:outline-none focus:ring-2 focus:ring-[#7B2CBF]/40" />
            <button onClick={submit} disabled={submitting} data-testid="review-submit-btn"
              className="mt-3 btn-gradient text-white px-5 py-2.5 rounded-full font-medium flex items-center gap-2 disabled:opacity-50 hover:-translate-y-0.5 transition-transform">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Post review"}
            </button>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {data?.reviews?.map((rv) => (
            <div key={rv.id} className="bg-white rounded-3xl soft-shadow p-6" data-testid={`review-${rv.id}`}>
              <Stars value={rv.rating} />
              <p className="text-[#3F3A56] mt-3 leading-relaxed">{rv.body}</p>
              <div className="text-sm text-[#9A95B5] mt-3">— {rv.author_name}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
